import { memo, useEffect, useRef, useState } from 'react';
import ScrollView from '@/sub-luca-plugin/compatible-components/scroll-view';
import { IScrollViewContext } from '@/sub-luca-plugin/compatible-components';
import { eventBusExample } from '@/sub-luca-plugin/utils/class-example';
import { query_questions } from '@/sub-luca-plugin/apis/my-question';
import { IMyQuestionItem, TResourceType } from '@/sub-luca-plugin/types';
import { getQaStore } from '@/sub-luca-plugin/store/qa';
import { delayExcuteCallback, isWeApp } from '@/sub-luca-plugin/utils';
import classNames from 'classnames';
import './index.scss';
import { getTopValue } from './helper';
import { tCommon } from '@/sub-luca-plugin/i18n';

interface IProps {
    visible: boolean;
    lucaAskChatId?: string;
    askId?: number;
    askChatTitle?: string;
    refreshCommRecordsByEffect?: string;
    onClose: () => void;
    onControlCommRecordsIconShow: (status: boolean) => void;
}

const CommRecordsDrawer = ({
    visible,
    lucaAskChatId,
    askId,
    askChatTitle,
    refreshCommRecordsByEffect,
    onClose,
    onControlCommRecordsIconShow,
}: IProps) => {
    const scrollRef = useRef<IScrollViewContext>();
    const pagination = useRef<{ pageSize: number; pageNum: number }>({ pageNum: 1, pageSize: 20 });
    const isNextPage = useRef(true);
    const isLoading = useRef(false);
    const [topValue, setTopValue] = useState('48px');
    const [total, setTotal] = useState(0);
    const [commRecords, setCommRecords] = useState<IMyQuestionItem[]>([]);

    const getList = async () => {
        isLoading.current = true;
        const { qaConfig } = getQaStore()!;
        const params: { resourceId?: number; courseId?: number; projectId?: number; resourceType?: TResourceType } = {};
        if (qaConfig.initResourceId) {
            params.resourceId = qaConfig.initResourceId;
            params.resourceType = qaConfig.initResourceType;
        } else if (qaConfig.initCourseId) {
            params.courseId = qaConfig.initCourseId;
        } else {
            params.projectId = qaConfig.projectId;
        }
        const { result } = await query_questions({ ...params, ...pagination.current });
        const { list, totalSize } = result;
        let askChatTitle = '';
        const filterList = (list || []).filter(item => {
            if (item.lucaAskChatId && item.lucaAskChatId === qaConfig.lucaAskChatId) {
                askChatTitle = item.askChatTitle;
                return false;
            }
            if (item.askId && item.askId === qaConfig.askId) {
                askChatTitle = item.askChatTitle;
                return false;
            }
            return true;
        });
        let newTotal = 0;
        setCommRecords(oldCommRecords => {
            const newQaConfig = { ...(qaConfig as IMyQuestionItem), askChatTitle };
            const newCommRecords =
                pagination.current.pageNum === 1
                    ? lucaAskChatId || askId
                        ? [newQaConfig, ...filterList]
                        : filterList
                    : oldCommRecords.concat(filterList);
            const len = newCommRecords.length;
            newTotal = totalSize || len;
            isNextPage.current = len < newTotal;
            return newCommRecords;
        });
        delayExcuteCallback(() => {
            setTotal(newTotal);
            onControlCommRecordsIconShow(newTotal > 0);
        }, 1);
        isLoading.current = false;
    };

    const handleScrollToLower = () => {
        if (isLoading.current || !isNextPage.current) return;
        pagination.current.pageNum++;
        getList();
    };

    const handleSwitchChat = ({ isLucaAsk, lucaAskChatId: selectedLucaAskChatId, askId: selectedAskId }: IMyQuestionItem) => {
        if (isLucaAsk === 1) {
            if (selectedLucaAskChatId !== lucaAskChatId) {
                eventBusExample.publish('QaInitOverlay', { type: 'initByLucaAskChatId', params: { lucaAskChatId: selectedLucaAskChatId } });
            }
        } else {
            if (selectedAskId !== askId) {
                eventBusExample.publish('QaInitOverlay', { type: 'initByAskId', params: { askId: selectedAskId } });
            }
        }
        onClose();
    };

    useEffect(() => {
        if (visible) {
            scrollRef.current?.scrollToTop();
        }
    }, [visible]);

    useEffect(() => {
        if (refreshCommRecordsByEffect) {
            pagination.current = { pageNum: 1, pageSize: 20 };
            getList();
        }
    }, [refreshCommRecordsByEffect]);

    useEffect(() => {
        if (askChatTitle) {
            setCommRecords(oldList =>
                oldList.map(item => {
                    if (item.lucaAskChatId !== lucaAskChatId) return item;
                    return { ...item, askChatTitle };
                })
            );
        }
    }, [askChatTitle]);

    useEffect(() => {
        getTopValue().then(top => setTopValue(top));
    }, []);

    return (
        <>
            <div
                className={classNames('qa-record-modal-overaly', visible && 'qa-record-modal-overaly--show')}
                style={{ top: topValue }}
                onClick={onClose}
            />
            <div
                className="qa-record-modal"
                style={{
                    maxHeight: visible ? '424px' : 0,
                    top: topValue,
                    transition: isWeApp ? 'none' : 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
            >
                <ScrollView
                    className="qa-record-modal__scroll-container"
                    lowerThreshold={100}
                    scrollY
                    onScrollToLower={handleScrollToLower}
                    ref={scrollRef}
                >
                    {commRecords.map(item => {
                        const detailId = item.lucaAskChatId || item.askId;
                        const selectedDetailId = lucaAskChatId || askId;
                        return (
                            <div
                                key={detailId}
                                className={classNames(
                                    'qa-record-modal__item',
                                    detailId === selectedDetailId && 'qa-record-modal__item--selected'
                                )}
                                onClick={() => handleSwitchChat(item)}
                            >
                                <div className="qa-record-modal__item-current-mark">
                                    <div>{tCommon.t('current', '当前')}</div>
                                </div>
                                <div className="qa-record-modal__item-category">{`${item.projectName} - ${item.subjectName}${
                                    item.twoLevelSubjectName ? ' - ' + item.twoLevelSubjectName : ''
                                }`}</div>
                                <div
                                    className={classNames(
                                        'qa-record-modal__item-describe',
                                        !item.askChatTitle && 'qa-record-modal__item-describe--gray'
                                    )}
                                >
                                    {item.askChatTitle || tCommon.t('noQuestionTopic', '暂无提问主题')}
                                </div>
                            </div>
                        );
                    })}
                    <div style={{ height: '12px' }}></div>
                </ScrollView>
                <div className="qa-record-modal__footer">
                    <div className="qa-record-modal__num">
                        {tCommon.translate({
                            key: 'totalCount',
                            variables: { total: total > 99 ? '99+' : `${total}` },
                            fallback: `共 ${total > 99 ? '99+' : `${total}`} 个`,
                        })}
                    </div>
                    <div className="qa-record-modal__close" onClick={onClose}>
                        {tCommon.t('collapse', '收起')}
                    </div>
                </div>
            </div>
        </>
    );
};

export default memo(CommRecordsDrawer);
